// App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { authenticatedFetch } from './utils/api';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Record from './pages/Record';
import Account from './pages/Account';
import Assessment from './pages/Assessment';
import Recordings from './pages/Recordings';
import Dashboard from './pages/Dashboard';
import PresentationView from './components/PresentationView/PresentationView';
import './App.css';
import Support from './components/Support/Support';
import Terms from './components/Legal/Terms';
import Privacy from './components/Legal/Privacy';
import CreateAndLearn from './pages/CreateAndLearn';
import Plans from './components/Plans/Plans';
import Header from './components/Header/Header';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import NotFound from './components/NotFound/NotFound';
import EmailVerification from './components/EmailVerification/EmailVerification';
import InstallPrompt from './components/InstallPrompt/InstallPrompt';
import AuthCallback from './components/GoogleAuthButton/AuthCallback';
import ResetPassword from './components/PasswordReset/ResetPassword';
import Leaderboard from './components/Leaderboard/Leaderboard';

export const PlansContext = React.createContext();

const LoadingScreen = () => (
  <div className='loadingScreen'>
    <div className='spinner' />
  </div>
);

const AppContent = () => {
  const { user, assessmentCompleted } = useAuth();
  const [showPlans, setShowPlans] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [initialRouteChecked, setInitialRouteChecked] = useState(false);

  useEffect(() => {
    if (user) {
      setInitialRouteChecked(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setShowInstallPrompt(false);
    }
  }, [user]);

  const fetchPlanData = async () => {
    try {
      setLoading(true);
      const data = await authenticatedFetch('/api/auth/limits');
      setPlanData(data);
    } catch (error) {
      console.error('Error fetching plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showPlans) {
      fetchPlanData();
    }
  }, [showPlans]);

  const handleLimitReached = () => {
    setShowPlans(true);
  };

  const ProtectedRoute = ({ children }) => {
    if (!user) return <Navigate to="/login" />;
    if (!initialRouteChecked) return <LoadingScreen />;

    // Only redirect to assessment if we explicitly know it's not completed
    if (assessmentCompleted === false && window.location.pathname !== '/assessment') {
      return <Navigate to="/assessment" />;
    }

    return children;
  };

  // Show loading screen while checking initial status
  if (user && !initialRouteChecked) {
    return <LoadingScreen />;
  }

  return (
    <>
      <PlansContext.Provider value={{ handleLimitReached }}>
        {true && !window.matchMedia('(display-mode: standalone)').matches && (
          <InstallPrompt
            show={showInstallPrompt}
            onClose={() => setShowInstallPrompt(false)}
          />
        )}
        <ScrollToTop />

        {user && <Header assessmentCompleted={assessmentCompleted} />}
        <main className="app-content">
          <Routes>
            {!user && <Route path="/" element={<Login />} />}
            {user && <Route path="/" element={
              assessmentCompleted === false ?
                <Navigate to="/assessment" /> :
                <Navigate to="/dashboard" />
            } />}

            <Route path="*" element={<NotFound />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/login" element={<Login onLoginSuccess={() => setShowInstallPrompt(true)} />} />

            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/assessment" element={
              <ProtectedRoute>
                <Assessment />
              </ProtectedRoute>
            } />
            <Route path="/practice" element={
              <ProtectedRoute>
                <Record />
              </ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } />
            <Route path="/recordings" element={
              <ProtectedRoute>
                <Recordings />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/presentations/:id" element={
              <ProtectedRoute>
                <PresentationView />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="/create" element={
              <ProtectedRoute>
                <CreateAndLearn />
              </ProtectedRoute>
            } />
            <Route path="/leaderboard" element={
              <ProtectedRoute>
                <Leaderboard />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
      </PlansContext.Provider>

      {showPlans && (
        <Plans
          onClose={() => {
            setShowPlans(false);
            setPlanData(null);
          }}
          currentPlan={planData}
          isLoading={loading}
        />
      )}
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;